import type { ActivationLifecycle } from '@/infra/activation'
import { LifecycleTracker } from '@/infra/lifecycle-tracker'
import { computed } from 'mobx'
import type { SubmittedJob } from '@/features/jobs/details/SubmittedJob'
import type { BookkeepingViewModel } from '@/features/jobs/details/bookkeeping-type/BookkeepingViewModel'
import {
  computeBookkeepingSteps,
  getCurrentStep,
} from '@/features/jobs/bookkeeping/bookkeeping-content'
import type { DefaultJobConversationViewModel } from '../JobConversationViewModel'
import { NEVER, type Observable } from 'rxjs'

export class BookkeepingJobDetailsViewModel implements ActivationLifecycle {
  private readonly _lifecycle = new LifecycleTracker()

  /**
   * Observable that emits when the user should be bounced from the bookkeeping job details.
   * Currently bookkeeping jobs don't have any bounce conditions.
   */
  readonly bounce$: Observable<unknown> = NEVER

  constructor(
    private readonly _isRatingsEnabled: () => boolean,
    private readonly _isRateable: () => boolean,
    public readonly job: SubmittedJob,
    public readonly jobConversationVm: DefaultJobConversationViewModel,
    public readonly bookkeepingVm: BookkeepingViewModel,
  ) {
    this._lifecycle.manage(this.bookkeepingVm)
  }

  /**
   * The bookkeeping progression info.
   */
  @computed
  get bookkeepingProgressionInfo() {
    return this.bookkeepingVm.bookkeepingProgressionInfo
  }

  /**
   * The current expected step.
   */
  @computed
  get currentBookkeepingStep() {
    return getCurrentStep(
      this.expectationSteps,
      this.bookkeepingProgressionInfo?.displayStage,
    )
  }

  /**
   * The steps of the bookkeeping expectations dialog.
   */
  @computed
  get expectationSteps() {
    return computeBookkeepingSteps({
      ratingEnabled: this._isRatingsEnabled(),
      currentStage: this.bookkeepingProgressionInfo?.displayStage,
    })
  }

  /** @inheritDoc */
  activate() {
    this._lifecycle.activate()
  }

  /** @inheritDoc */
  deactivate() {
    this._lifecycle.deactivate()
  }

  /**
   * Fetches data needed for the view to be able to render.
   */
  async fetch(_signal?: AbortSignal) {
    // Nothing to fetch for bookkeeping.
  }
}
